import React, { createContext, useContext, useState } from 'react'
import { LayoutWithKeyPath, keyFromPath } from '../../../utils/templateBuilderUtils'
import { useTemplate } from '../TemplateProvider'

interface TemplateLayoutContextType {
  clipboardLayoutKeyPath?: string
  parentLayout?: LayoutWithKeyPath
  rootLayout?: LayoutWithKeyPath
  selectedLayout?: LayoutWithKeyPath
  setClipboardLayoutKeyPath: (keyPath: string) => void
  setRootLayoutKey: (key?: number) => void
  setSelectedKeyPath: (key?: string) => void
  setLayout: (body: LayoutWithKeyPath) => void
}

const TemplateLayoutContext = createContext<TemplateLayoutContextType>({} as any)

export function useTemplateLayout() {
  const context = useContext(TemplateLayoutContext)
  if (!context) {
    throw new Error('useTemplateLayout must be used within a TemplateLayoutProvider')
  }
  return context
}

interface ProviderProps {
  children: React.ReactNode
}

export default function TemplateLayoutProvider({ children }: ProviderProps) {
  const { template, updateTemplate } = useTemplate()

  const [rootLayoutKey, setRootLayoutKey] = useState<number>()
  const [selectedKeyPath, setSelectedKeyPath] = useState<string>()
  const [clipboardLayoutKeyPath, setClipboardLayoutKeyPath] = useState<string>()

  const rootLayout =
    rootLayoutKey != null
      ? ({
          keyPath: `${rootLayoutKey}`,
          ...template?.layoutMap?.[rootLayoutKey],
        } as LayoutWithKeyPath)
      : undefined
  const selectedKey = selectedKeyPath != null && Number(selectedKeyPath?.split('/').pop())
  const selectedLayout = selectedKey
    ? ({
        keyPath: selectedKeyPath,
        ...template?.layoutMap?.[Number(selectedKey)],
      } as LayoutWithKeyPath)
    : undefined

  const parentKey =
    selectedKeyPath != null ? selectedKeyPath.split('/').slice(-2, -1)[0] : undefined
  const parentLayout = parentKey
    ? ({
        keyPath: selectedKeyPath?.split('/').slice(0, -1).join('/'),
        ...template?.layoutMap?.[Number(parentKey)],
      } as LayoutWithKeyPath)
    : undefined

  function setLayout({ keyPath, ...body }: LayoutWithKeyPath) {
    const key = keyFromPath(keyPath)
    if (!key || !template?.layoutMap?.[key]) return

    updateTemplate?.({ layoutMap: { ...template.layoutMap, [key]: body } })
  }

  return (
    <TemplateLayoutContext.Provider
      value={{
        clipboardLayoutKeyPath,
        parentLayout,
        rootLayout,
        selectedLayout,
        setClipboardLayoutKeyPath,
        setLayout,
        setRootLayoutKey,
        setSelectedKeyPath,
      }}
    >
      {children}
    </TemplateLayoutContext.Provider>
  )
}
